import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { fetchClubs, fetchUsers } from "../clients/ClubClient";
import { Table } from "../components/Table";
import CreateGameModal from "../components/CreateGameModal";
import { fetchClubLeaderBoards, fetchGlobalLeaderBoards } from "../clients/LeaderBoardClient";
import { GameVariants, TimeVariants } from "../utils/gameEnums";
import Dropdown from "../components/DropDown";
import "../LeaderBoards.css";

const LeaderBoards = () => {
    const navigate = useNavigate()
    // const { someParam } = useParams();
    // const [searchParams] = useSearchParams();

    const pageNo = useRef(1)
    const [gameVariant, setGameVariant] = useState(GameVariants.STANDARD)
    const [timeVariant, setTimeVariant] = useState(TimeVariants.RAPID.value)
    const numberOfRows = 25;

    const DEFAULT_STATE = { name: "global", id: null }
    const [filterOptions, setFilterOptions] = useState([DEFAULT_STATE])
    const [selectedFilter, setSelectedFilter] = useState(DEFAULT_STATE)
    const [showClubElos, setShowClubElos] = useState(false)

    const [rows, setRows] = useState([])
    const [lastPage, setLastPage] = useState(true)

    useEffect(() => {
        fetchAndUpdateLeaderBoards()
        loadFilterOptions()
    }, []);

    //TODO start using context provider, so we dont need to "waste" a call when we are not logged in
    const loadFilterOptions = () => {
        fetchClubs()
            .then((clubs) => {
                setFilterOptions([
                    DEFAULT_STATE,
                    ...clubs.map((club) => {
                        return { name: club.clubName, id: club.clubId }
                    }),
                ]);
            })
            .catch((error) => {
                console.log("Not logged in - so cannot retrieve clubs. Error was: " + error)
            });
    };

    const fetchAndUpdateLeaderBoards = () => {
        let leaderBoardPromise;
        if (!clubFilterActive()) {
            leaderBoardPromise = fetchGlobalLeaderBoards(gameVariant, timeVariant, pageNo.current)
        } else {
            leaderBoardPromise = fetchClubLeaderBoards(
                gameVariant,
                timeVariant,
                pageNo.current,
                selectedFilter.id,
                showClubElos
            )
        }

        leaderBoardPromise
            .then((data) => {
                setRows(data.rows);
                setLastPage(data.lastPage)
            })
            .catch((e) => console.error("Something went wrong: ", e))
    };

    const clubFilterActive = () => {
        return selectedFilter.name && selectedFilter.name !== "global"
    };

    const handleNextPage = () => {
        if (!lastPage) {
            pageNo.current += 1;
            fetchAndUpdateLeaderBoards();
        }
    };

    const handlePreviousPage = () => {
        if (pageNo.current > 1) {
            pageNo.current -= 1;
        }
        fetchAndUpdateLeaderBoards()
    };

    //the purpose of this is to separate the data into several tables that act as columns such that we fill up the entire page
    const convertToTableColumns = (data) => {
        const splitData = []

        //kind stupid, but the first iteration of the loop will be true, and set the index to 0
        let tableIndex = -1

        data.forEach((row, index) => {
            if (index % numberOfRows === 0) {
                splitData.push([])
                tableIndex++;
            }
            splitData[tableIndex].push(row);
        });

        const rowStyle = { padding: "0.5rem" };

        return splitData.map((tableData, idx) => (
            <Table
                key={idx}
                headers={["rank", "username", "elo"]}
                data={tableData}
                onRowClick={(row) => navigate(`/profile?username=${row.username}`)}
                rowStyles={rowStyle}
            />
        ));
    };

    // card component for new grid layout
    const Card = ({ row }) => {
        const { rank, username, elo, avatarUrl } = row;
        const topClass = rank === 1 ? "top-1" : rank === 2 ? "top-2" : rank === 3 ? "top-3" : "";

        return (
            <div
                className={`leaderboard-card ${topClass}`}
                onClick={() => navigate(`/profile?username=${username}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/profile?username=${username}`)}
            >
                <span className="leaderboard-card__rank">{rank}</span>

                {avatarUrl ? (
                    <img src={avatarUrl} alt="avatar" className="leaderboard-card__avatar" />
                ) : (
                    <div className="leaderboard-card__avatar">
                        {username.slice(0, 2).toUpperCase()}
                    </div>
                )}

                <span className="leaderboard-card__username">{username}</span>
                <span className="leaderboard-card__elo">{elo}</span>
            </div>
        );
    };

    return (
        <div className="leaderboard-page">
            <div className="leaderboard-container">
                <div className="filters">
                    <Dropdown
                        values={Object.values(GameVariants)}
                        label="Game Variant"
                        handleUpdate={(gv) => setGameVariant(gv)}
                    />
                    <Dropdown
                        values={Object.values(TimeVariants).map(({ value }) => value)}
                        label="Time Variant"
                        handleUpdate={(tv) => setTimeVariant(tv)}
                    />
                    <Dropdown
                        values={filterOptions.map((opt) => opt.name)}
                        label="Filter"
                        handleUpdate={(name) =>
                            setSelectedFilter(filterOptions.find((opt) => opt.name === name))
                        }
                    />
                    {clubFilterActive() && (
                        <Dropdown
                            values={["Global", "Club"]}
                            label="Elo Type"
                            handleUpdate={(v) => setShowClubElos(v === "Club")}
                        />
                    )}
                    <button className="refresh-button" onClick={fetchAndUpdateLeaderBoards}>
                        Get Leaderboards
                    </button>

                    <div className="pagination">
                        <button
                            className="pagination__button"
                            style={{ visibility: pageNo.current > 1 ? "visible" : "hidden" }}
                            onClick={handlePreviousPage}
                        >
                            Previous
                        </button>
                        <button
                            className="pagination__button"
                            style={{ visibility: lastPage ? "hidden" : "visible" }}
                            onClick={handleNextPage}
                        >
                            Next
                        </button>
                    </div>
                </div>

                {rows.length > 0 ? (
                    <div className="leaderboard-list">
                        {rows.map((r) => (
                            <Card key={r.username} row={r} />
                        ))}

                        {/* original table layout kept for reference; set to true to render */}
                        {false && <div style={{ display: "flex" }}>{convertToTableColumns(rows)}</div>}
                    </div>
                ) : (
                    <p className="no-results">No Leaderboard Found - Try Changing the Filters</p>
                )}
            </div>
        </div>
    );
};

export default LeaderBoards
